import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  searchProducts,
  searchBundles,
  searchModifiers,
  searchOptionValues,
  type ProductSearchResult,
} from '../api/products';
import type { LiveMenuItem, LiveMenuSummary } from '../api/liveMenuItems';
import { useLiveMenuItems } from './queries';
import type { BlackoutItem, BlackoutType } from '../components/ItemAvailability/types';

/**
 * Option source for the snooze item picker, shared by the desktop
 * ItemAvailability page and MobileSnoozeItems.
 *
 * Two modes:
 * - 'menu'   — the store's live published menus loaded in the background; the
 *              Autocomplete filters the full list locally (browsable on focus,
 *              no network per keystroke). Items already snoozed at the store
 *              are excluded. Only items actually live at the store appear.
 * - 'search' — the original catalog-wide debounced search. Used while the menu
 *              is loading, when it fails, when the live menu has no items of
 *              the selected type, or when the user toggles "Search full
 *              catalog".
 */

export type SnoozeOptionsMode = 'menu' | 'search';
export type MenuStatus = 'loading' | 'ready' | 'partial' | 'empty' | 'error';

export interface UseSnoozeItemOptionsArgs {
  storeNumber: string | null | undefined;
  itemType: BlackoutType;
  blackouts: BlackoutItem[];
}

export interface UseSnoozeItemOptionsResult {
  mode: SnoozeOptionsMode;
  options: ProductSearchResult[];
  /**
   * Menu-mode options matching the current text filter — drives the
   * "Select all shown" affordance. Mirrors MUI's substring filter on
   * `${code} ${name}` so select-all respects what the user narrowed to.
   * Empty in search mode, where the server already filtered.
   */
  visibleMenuOptions: ProductSearchResult[];
  inputValue: string;
  onInputChange: (value: string) => void;
  isLoading: boolean;
  menuStatus: MenuStatus;
  useCatalogSearch: boolean;
  setUseCatalogSearch: (value: boolean) => void;
  menus: LiveMenuSummary[];
  partial: boolean;
  reset: () => void;
}

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_CHARS = 1;

const TYPE_TO_LIST_KEY: Partial<
  Record<BlackoutType, 'products' | 'bundles' | 'modifiers' | 'optionValues'>
> = {
  PRODUCT: 'products',
  BUNDLE: 'bundles',
  MODIFIER: 'modifiers',
  OPTION_VALUE: 'optionValues',
};

const TYPE_TO_BLACKOUT_CODE_KEY: Partial<Record<BlackoutType, keyof BlackoutItem>> = {
  PRODUCT: 'productCode',
  BUNDLE: 'bundleCode',
  MODIFIER: 'modifierCode',
  OPTION_VALUE: 'optionValueCode',
};

async function searchByType(itemType: BlackoutType, query: string): Promise<ProductSearchResult[]> {
  switch (itemType) {
    case 'PRODUCT':
      return searchProducts(query);
    case 'BUNDLE':
      return searchBundles(query);
    case 'MODIFIER':
      return searchModifiers(query);
    case 'OPTION_VALUE':
      return searchOptionValues(query);
    // VARIANT is reached by drilling into a product, never searched directly.
    default:
      return [];
  }
}

export function useSnoozeItemOptions({
  storeNumber,
  itemType,
  blackouts,
}: UseSnoozeItemOptionsArgs): UseSnoozeItemOptionsResult {
  const menuQuery = useLiveMenuItems(storeNumber);

  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState<ProductSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [useCatalogSearch, setUseCatalogSearch] = useState(false);

  // Codes snoozed at this store for the selected type. Only NON-EXPIRED
  // blackouts hide an item; variant-level blackouts never hide the parent
  // product (user may want to snooze more variants or the whole product).
  const snoozedCodes = useMemo(() => {
    const codeKey = TYPE_TO_BLACKOUT_CODE_KEY[itemType];
    const set = new Set<string>();
    if (!codeKey) return set;
    const now = Date.now();
    for (const blackout of blackouts) {
      const code = blackout[codeKey];
      if (typeof code !== 'string' || !code) continue;
      if (blackout.expiry && new Date(blackout.expiry).getTime() <= now) continue;
      set.add(code);
    }
    return set;
  }, [blackouts, itemType]);

  const typeList: LiveMenuItem[] = useMemo(() => {
    const listKey = TYPE_TO_LIST_KEY[itemType];
    if (!listKey || !menuQuery.data) return [];
    return menuQuery.data[listKey];
  }, [menuQuery.data, itemType]);

  // Effective mode. Empty type list keeps that type on catalog search (e.g. a
  // menu that carries no modifiers), which is also the loading/error fallback.
  const mode: SnoozeOptionsMode =
    menuQuery.isSuccess && !useCatalogSearch && typeList.length > 0 ? 'menu' : 'search';

  // Debounced catalog search — only active in search mode.
  useEffect(() => {
    if (mode !== 'search') {
      return;
    }
    if (inputValue.length < MIN_SEARCH_CHARS) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await searchByType(itemType, inputValue);
        if (!cancelled) setSearchResults(results);
      } catch (err) {
        console.error('Search error:', err);
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [inputValue, itemType, mode]);

  const options = useMemo(() => {
    if (mode === 'menu') {
      return typeList.filter((item) => !snoozedCodes.has(item.code));
    }
    // Consistency win over the old flow: snoozed items drop out of catalog
    // search results too.
    return searchResults.filter((item) => !snoozedCodes.has(item.code));
  }, [mode, typeList, searchResults, snoozedCodes]);

  const visibleMenuOptions = useMemo(() => {
    if (mode !== 'menu') return [];
    const q = inputValue.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => `${o.code} ${o.name}`.toLowerCase().includes(q));
  }, [mode, options, inputValue]);

  const menuStatus: MenuStatus = useMemo(() => {
    if (menuQuery.isLoading) return 'loading';
    if (menuQuery.isError) return 'error';
    if (!menuQuery.data) return 'loading';
    if (menuQuery.data.menus.length === 0 || typeList.length === 0) return 'empty';
    if (menuQuery.data.partial || menuQuery.data.truncated) return 'partial';
    return 'ready';
  }, [menuQuery.isLoading, menuQuery.isError, menuQuery.data, typeList.length]);

  const reset = useCallback(() => {
    setInputValue('');
    setSearchResults([]);
  }, []);

  return {
    mode,
    options,
    visibleMenuOptions,
    inputValue,
    onInputChange: setInputValue,
    isLoading: mode === 'search' ? isSearching : false,
    menuStatus,
    useCatalogSearch,
    setUseCatalogSearch,
    menus: menuQuery.data?.menus ?? [],
    partial: Boolean(menuQuery.data?.partial || menuQuery.data?.truncated),
    reset,
  };
}
